// src/components/Auth/Welcome.js
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Box,
  Fade,
  Zoom,
  Grow,
} from '@mui/material';
import api from '../../services/api';

export default function Welcome() {
  const navigate = useNavigate();
  const { authData } = useContext(AuthContext);
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authData?.access_token) {
      navigate('/login');
      return;
    }

    // Simulate progress bar
    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressTimer);
          return 100;
        }
        return prev + 10;
      });
    }, 300);

    // Check verification status
    const checkStatus = async () => {
      try {
        const response = await api.get('/api/dashboard', {
          headers: { Authorization: `Bearer ${authData.access_token}` }
        });
        const status = response.data.profile.verificationStatus;

        if (status === 'verified' || status === 'failed') {
          setVerificationStatus(status);
        }
      } catch (error) {
        console.error('Error checking status:', error);
      }
    };

    // Check immediately
    checkStatus();

    // Then check every 3 seconds
    const statusInterval = setInterval(checkStatus, 3000);

    return () => {
      clearInterval(progressTimer);
      clearInterval(statusInterval);
    };
  }, [navigate, authData]);

  const handleContinue = () => {
    // Mark that user has seen welcome, then navigate to dashboard
    // Dashboard will show onboarding automatically for first-time users
    localStorage.setItem('hasSeenWelcome', 'true');
    navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          opacity: 0.1,
          background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
        }}
      />
      
      <Fade in timeout={800}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 600,
            mx: { xs: 2, sm: 4 },
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Card
            elevation={12}
            sx={{
              background: 'rgba(255, 255, 255, 0.98)',
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            <CardContent sx={{ p: { xs: 4, sm: 6 } }}>
            <Stack spacing={4} alignItems="center">
              {/* Animated Icon with glow effect */}
              <Zoom in timeout={1000}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Pulsing glow rings */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      background: 'rgba(30, 136, 229, 0.2)',
                      animation: 'pulse-ring 2s ease-out infinite',
                      '@keyframes pulse-ring': {
                        '0%': {
                          transform: 'scale(0.8)',
                          opacity: 1,
                        },
                        '100%': {
                          transform: 'scale(1.4)',
                          opacity: 0,
                        },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      background: 'rgba(30, 136, 229, 0.2)',
                      animation: 'pulse-ring 2s ease-out infinite 1s',
                    }}
                  />
                  {/* Icon container */}
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 10px 40px rgba(30, 136, 229, 0.4)',
                      animation: 'float 3s ease-in-out infinite',
                      '@keyframes float': {
                        '0%, 100%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-10px)' },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/logo.svg"
                      alt="OnlyStats Logo"
                      sx={{ 
                        width: 70,
                        height: 70,
                        objectFit: 'contain',
                        filter: 'brightness(0) invert(1)',
                      }} 
                    />
                  </Box>
                </Box>
              </Zoom>
              
              <Grow in timeout={1200}>
                <Stack spacing={2} alignItems="center">
                  <Typography 
                    variant="h3" 
                    align="center" 
                    fontWeight={800}
                    sx={{
                      color: '#1e88e5',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    Welcome to OnlyStats! 🎉
                  </Typography>

                  <Typography 
                    variant="h6" 
                    align="center" 
                    sx={{
                      color: '#666',
                      fontWeight: 400,
                      maxWidth: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    Your account has been created successfully. Let's get you started with a quick tour of the platform!
                  </Typography>
                </Stack>
              </Grow>

              <Fade in timeout={1500}>
                <Stack direction="column" spacing={2} sx={{ width: '100%', mt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleContinue}
                    sx={{
                      py: 1.8,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                      color: 'white',
                      borderRadius: 3,
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(30, 136, 229, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 32px rgba(30, 136, 229, 0.5)',
                      },
                    }}
                  >
                    Start Onboarding →
                  </Button>
                  
                  <Typography 
                    variant="body2" 
                    align="center" 
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    Explore OnlyStats and manage your subscriptions.
                  </Typography>
                </Stack>
              </Fade>
            </Stack>
            </CardContent>
          </Card>
        </Box>
      </Fade>
      
      {/* Background decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(80px)',
        }}
      />
    </Box>
  );
}
